import React from 'react';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentCheckIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TagIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import type { DocumentDetail, DocumentVersion } from '../types/documentVersion.types';

type GateTone = 'complete' | 'attention' | 'blocked' | 'optional';

interface LifecycleGate {
  label: string;
  detail: string;
  tone: GateTone;
}

interface LifecycleStep {
  label: string;
  detail: string;
  state: 'done' | 'current' | 'next' | 'later';
}

interface LifecycleAction {
  label: string;
  detail: string;
  tone?: 'primary' | 'standard' | 'warning';
  onClick: () => void;
}

interface WorkflowPanelProps {
  document?: DocumentDetail | null;
  versions: DocumentVersion[];
  documentTags: string[];
  onOpenCollaborators: () => void;
  onOpenLatestVersion?: () => void;
  onGoToDocumentInfo: () => void;
  onGoToProcessing: () => void;
  onGoToCompare: () => void;
  onGoToVersionHistory: () => void;
}

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not recorded';
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const versionLabel = (version?: DocumentVersion | null, fallback?: string | number | null) => {
  const raw = version?.version_number ?? fallback;
  if (raw === undefined || raw === null || raw === '') return 'No version';
  const label = String(raw);
  return label.toLowerCase().startsWith('v') ? label : `v${label}`;
};

const latestActivityDate = (document?: DocumentDetail | null, version?: DocumentVersion | null) => (
  document?.effective_modified_at ||
  version?.checked_in_at ||
  version?.updated_at ||
  version?.uploaded_at ||
  document?.updated_at ||
  document?.created_at
);

const hasSignatureEvidence = (versions: DocumentVersion[]) => (
  versions.some((version) => Boolean(version.checkin_metadata?.signature_audit))
);

const hasTrackingSummary = (versions: DocumentVersion[]) => (
  versions.some((version) => Boolean(version.checkin_metadata?.tracking_summary))
);

const includesAny = (value: string, patterns: RegExp[]) => (
  patterns.some((pattern) => pattern.test(value))
);

const gateClass = (tone: GateTone) => {
  switch (tone) {
    case 'complete':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/70 dark:bg-emerald-950/30 dark:text-emerald-200';
    case 'blocked':
      return 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-200';
    case 'attention':
      return 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-200';
    default:
      return 'border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] text-[var(--brand-text-secondary)]';
  }
};

const gateIcon = (tone: GateTone) => {
  if (tone === 'complete') return <CheckCircleIcon className="h-4 w-4 flex-none" />;
  if (tone === 'blocked') return <ExclamationTriangleIcon className="h-4 w-4 flex-none" />;
  if (tone === 'attention') return <ClockIcon className="h-4 w-4 flex-none" />;
  return <ShieldCheckIcon className="h-4 w-4 flex-none" />;
};

export const WorkflowPanel: React.FC<WorkflowPanelProps> = ({
  document,
  versions,
  documentTags,
  onOpenCollaborators,
  onOpenLatestVersion,
  onGoToDocumentInfo,
  onGoToProcessing,
  onGoToCompare,
  onGoToVersionHistory,
}) => {
  const latestVersion = versions[0] || null;
  const searchableProfile = [
    document?.title,
    document?.type,
    document?.category_name,
    document?.category_type,
    document?.matter_description,
    document?.client_name,
    latestVersion?.original_name,
    latestVersion?.changes_description,
    ...documentTags,
  ].filter(Boolean).join(' ').toLowerCase();

  const hasVersions = versions.length > 0 || Boolean(document?.latest_version);
  const hasClient = Boolean(document?.client_id || document?.client_name);
  const hasMatter = Boolean(document?.matter_id || document?.matter_description);
  const hasDocumentType = Boolean(document?.category_id || document?.category_name);
  const isCheckedOut = Boolean(document?.is_checked_out);
  const agreementLike = includesAny(searchableProfile, [/engagement/, /fee agreement/, /retainer/, /contract/, /signature/]);
  const privilegeReviewed = documentTags.some((tag) => /privileg|work product|confidential|protected/i.test(tag));
  const trackingCaptured = hasTrackingSummary(versions);
  const signatureCaptured = hasSignatureEvidence(versions);
  const modifiedAt = latestActivityDate(document, latestVersion);

  const lifecycleStage = (() => {
    if (!hasVersions) return { label: 'Intake shell', detail: 'No official DMS version exists yet.' };
    if (isCheckedOut) return { label: 'Active drafting', detail: 'A working copy is checked out and should be checked in before final review.' };
    if (!hasDocumentType || !hasClient || !hasMatter) return { label: 'Classification needed', detail: 'The document needs ledger metadata before downstream AI and workflow decisions are reliable.' };
    if (agreementLike && !signatureCaptured) return { label: 'Ready for execution review', detail: 'Agreement-like document is classified, but signature evidence has not been captured.' };
    if (signatureCaptured) return { label: 'Executed evidence captured', detail: 'A signed or signature-audited version exists in the DMS ledger.' };
    return { label: 'Review ready', detail: 'Core DMS metadata is complete and the latest version is ready for review.' };
  })();

  const gates: LifecycleGate[] = [
    {
      label: 'Official version',
      detail: hasVersions ? `${versionLabel(latestVersion, document?.latest_version?.version_number)} is available.` : 'Create the first official DMS version.',
      tone: hasVersions ? 'complete' : 'blocked',
    },
    {
      label: 'Document type',
      detail: hasDocumentType ? `${document?.category_name} is set.` : 'Set a legal document type so retention, retrieval, and review logic can reason about this file.',
      tone: hasDocumentType ? 'complete' : 'blocked',
    },
    {
      label: 'Client linkage',
      detail: hasClient ? `Linked to ${document?.client_name || 'client record'}.` : 'Assign the client so ownership and access context are clear.',
      tone: hasClient ? 'complete' : 'attention',
    },
    {
      label: 'Matter scope',
      detail: hasMatter ? `Scoped to ${document?.matter_description || 'matter record'}.` : 'Linking a matter gives Lex safe retrieval boundaries.',
      tone: hasMatter ? 'complete' : 'attention',
    },
    {
      label: 'Checkout state',
      detail: isCheckedOut ? `Checked out since ${formatDateTime(document?.checked_out_at)}.` : 'No active checkout detected.',
      tone: isCheckedOut ? 'attention' : 'complete',
    },
    {
      label: 'Tracked edit signal',
      detail: trackingCaptured ? 'Word tracking summary exists on at least one version.' : 'No Track Changes summary has been imported yet.',
      tone: trackingCaptured ? 'complete' : 'optional',
    },
    {
      label: 'Execution evidence',
      detail: signatureCaptured ? 'Signature/audit evidence is captured.' : agreementLike ? 'Agreement-like document should capture signature evidence before finalization.' : 'Not required unless this becomes an execution document.',
      tone: signatureCaptured ? 'complete' : agreementLike ? 'attention' : 'optional',
    },
    {
      label: 'Privilege / sensitivity',
      detail: privilegeReviewed ? 'Sensitive-document tag is present.' : 'Add a privilege or confidentiality tag when this file should be retrieval/export guarded.',
      tone: privilegeReviewed ? 'complete' : 'optional',
    },
  ];

  const completeGates = gates.filter((gate) => gate.tone === 'complete').length;
  const requiredGates = gates.filter((gate) => gate.tone !== 'optional');
  const requiredComplete = requiredGates.filter((gate) => gate.tone === 'complete').length;
  const readinessPercent = requiredGates.length ? Math.round((requiredComplete / requiredGates.length) * 100) : 0;

  const steps: LifecycleStep[] = [
    {
      label: 'DMS record',
      detail: hasVersions ? 'Version ledger created' : 'Awaiting first version',
      state: hasVersions ? 'done' : 'current',
    },
    {
      label: 'Classify',
      detail: hasDocumentType && (hasClient || hasMatter) ? 'Metadata usable' : 'Needs legal context',
      state: hasDocumentType && (hasClient || hasMatter) ? 'done' : hasVersions ? 'current' : 'next',
    },
    {
      label: 'Review',
      detail: trackingCaptured || versions.length > 1 ? 'Review signals available' : 'Compare or import tracking',
      state: trackingCaptured || versions.length > 1 ? 'done' : hasDocumentType ? 'current' : 'later',
    },
    {
      label: 'Approve',
      detail: agreementLike ? 'Attorney/client approval path' : 'Internal approval path',
      state: signatureCaptured ? 'done' : hasMatter ? 'next' : 'later',
    },
    {
      label: 'Finalize',
      detail: signatureCaptured ? 'Evidence captured' : 'Needs final DMS version',
      state: signatureCaptured ? 'done' : 'later',
    },
  ];

  const actions: LifecycleAction[] = [
    ...(!hasDocumentType || !hasClient || !hasMatter ? [{
      label: 'Fix ledger metadata',
      detail: 'Set document type, client, and matter scope.',
      tone: 'primary' as const,
      onClick: onGoToDocumentInfo,
    }] : []),
    ...(isCheckedOut && onOpenLatestVersion ? [{
      label: 'Continue edit session',
      detail: 'Open the working copy path and check it in when finished.',
      tone: 'primary' as const,
      onClick: onOpenLatestVersion,
    }] : []),
    ...(versions.length >= 2 ? [{
      label: 'Compare versions',
      detail: 'Review formatting and legal-context differences.',
      tone: 'standard' as const,
      onClick: onGoToCompare,
    }] : []),
    {
      label: 'Tune AI readiness',
      detail: 'Manage tags and retrieval readiness.',
      tone: hasMatter && hasDocumentType ? 'standard' : 'warning',
      onClick: onGoToProcessing,
    },
    {
      label: 'Invite reviewers',
      detail: 'Add collaborators when review needs another person.',
      tone: 'standard',
      onClick: onOpenCollaborators,
    },
    {
      label: 'Review version ledger',
      detail: 'Inspect official versions, checkout state, and downloads.',
      tone: 'standard',
      onClick: onGoToVersionHistory,
    },
  ];

  return (
    <div className="min-w-0 space-y-6 text-[var(--brand-text-primary)]">
      <section className="rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-lex-accent">
              <DocumentCheckIcon className="h-4 w-4" />
              Document Lifecycle
            </div>
            <h4 className="text-xl font-semibold text-[var(--brand-text-primary)]">{lifecycleStage.label}</h4>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-[var(--brand-text-secondary)]">{lifecycleStage.detail}</p>
          </div>
          <div className="w-full rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] p-4 lg:w-64">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-text-muted)]">Readiness</span>
              <span className="text-lg font-semibold text-lex-accent">{readinessPercent}%</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--brand-border-subtle)]">
              <div className="h-full rounded-full bg-lex-accent transition-all" style={{ width: `${readinessPercent}%` }} />
            </div>
            <p className="mt-3 text-xs text-[var(--brand-text-secondary)]">
              {requiredComplete}/{requiredGates.length} required gates complete, {completeGates}/{gates.length} total signals green.
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-4">
          <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-text-muted)]">Latest version</p>
            <p className="mt-1 font-semibold">{versionLabel(latestVersion, document?.latest_version?.version_number)}</p>
          </div>
          <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-text-muted)]">Last DMS activity</p>
            <p className="mt-1 font-semibold">{formatDateTime(modifiedAt)}</p>
          </div>
          <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-text-muted)]">Legal type</p>
            <p className="mt-1 font-semibold">{document?.category_name || 'Not classified'}</p>
          </div>
          <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-text-muted)]">Retrieval scope</p>
            <p className="mt-1 font-semibold">{hasMatter ? 'Matter scoped' : 'Unassigned document'}</p>
          </div>
        </div>
      </section>

      <section className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] p-5">
          <div className="mb-4 flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5 text-lex-accent" />
            <h5 className="font-semibold">Readiness Gates</h5>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {gates.map((gate) => (
              <div key={gate.label} className={`rounded-xl border p-3 ${gateClass(gate.tone)}`}>
                <div className="flex items-center gap-2">
                  {gateIcon(gate.tone)}
                  <span className="font-semibold">{gate.label}</span>
                </div>
                <p className="mt-2 text-sm leading-5 opacity-90">{gate.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] p-5">
          <div className="mb-4 flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-lex-accent" />
            <h5 className="font-semibold">Next Best Actions</h5>
          </div>
          <div className="space-y-2">
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                className={`group w-full rounded-xl border p-3 text-left transition ${
                  action.tone === 'primary'
                    ? 'border-lex-accent bg-lex-accent-soft text-lex-accent hover:bg-lex-state-hover'
                    : action.tone === 'warning'
                      ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-200'
                      : 'border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] text-[var(--brand-text-primary)] hover:border-lex-accent hover:bg-lex-accent-soft'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold">{action.label}</p>
                    <p className="mt-1 text-sm opacity-80">{action.detail}</p>
                  </div>
                  <ArrowRightIcon className="mt-1 h-4 w-4 flex-none transition group-hover:translate-x-0.5" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[1fr_1fr]">
        <div className="rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] p-5">
          <div className="mb-5 flex items-center gap-2">
            <DocumentMagnifyingGlassIcon className="h-5 w-5 text-lex-accent" />
            <h5 className="font-semibold">Lifecycle Path</h5>
          </div>
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div key={step.label} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold ${
                    step.state === 'done'
                      ? 'border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200'
                      : step.state === 'current'
                        ? 'border-lex-accent bg-lex-accent-soft text-lex-accent'
                        : 'border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] text-[var(--brand-text-muted)]'
                  }`}>
                    {step.state === 'done' ? <CheckCircleIcon className="h-4 w-4" /> : index + 1}
                  </div>
                  {index < steps.length - 1 && <div className="mt-1 h-8 w-px bg-[var(--brand-border)]" />}
                </div>
                <div className="min-w-0 pb-2">
                  <p className="font-semibold">{step.label}</p>
                  <p className="text-sm text-[var(--brand-text-secondary)]">{step.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] p-5">
          <div className="mb-4 flex items-center gap-2">
            <TagIcon className="h-5 w-5 text-lex-accent" />
            <h5 className="font-semibold">Governance Snapshot</h5>
          </div>
          <div className="space-y-3 text-sm">
            <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] p-3">
              <div className="flex items-center gap-2 font-semibold">
                <LockClosedIcon className="h-4 w-4 text-lex-accent" />
                Retrieval safety
              </div>
              <p className="mt-2 text-[var(--brand-text-secondary)]">
                {hasMatter
                  ? 'This document can participate in matter-scoped retrieval and downstream AI review.'
                  : 'Keep this searchable by DMS metadata and tags, but do not expose it to a matter-scoped AI answer until it is linked.'}
              </p>
            </div>
            <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] p-3">
              <div className="flex items-center gap-2 font-semibold">
                <PencilSquareIcon className="h-4 w-4 text-lex-accent" />
                Edit integrity
              </div>
              <p className="mt-2 text-[var(--brand-text-secondary)]">
                {isCheckedOut
                  ? 'The current truth is a working copy until the edit session is checked in as an official DMS version.'
                  : 'The current truth is the latest checked-in DMS version.'}
              </p>
            </div>
            <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] p-3">
              <div className="flex items-center gap-2 font-semibold">
                <UserGroupIcon className="h-4 w-4 text-lex-accent" />
                Human review
              </div>
              <p className="mt-2 text-[var(--brand-text-secondary)]">
                {agreementLike
                  ? 'Agreement-like documents should move through attorney review, client approval, and signature evidence capture.'
                  : 'Use collaborators and version comparison when another reviewer needs to approve the document before finalization.'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
